import { __rest } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import { format, isValid, isWithinInterval, parse } from 'date-fns';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useState } from 'react';
import { createAutoCorrectedDatePipe } from 'text-mask-addons';
import { useI18n } from '../../../../context/occ/hooks/use-i18n';
import { Input, InputMode } from '../input';
/**
 * Renders the date input component
 */
export const DateInput = observer(props => {
    const i18n = useI18n();
    const { dateFormat, onChangeValue, value } = props, inputProps = __rest(props, ["dateFormat", "onChangeValue", "value"]);
    const [date, setDate] = useState('');
    const minYear = props.minYear || 1900;
    const maxYear = props.maxYear || new Date().getFullYear();
    const dateFormatLowerCase = dateFormat.toLowerCase();
    const mask = getDateFormatMask(dateFormatLowerCase);
    const pipe = createAutoCorrectedDatePipe(dateFormatLowerCase, {
        minYear: minYear,
        maxYear: maxYear
    });
    const handleDateChange = (event) => {
        setDate(event.currentTarget.value);
    };
    const handleDateValueChange = useCallback(() => {
        const parsedDate = parseDateString(date, dateFormat);
        const newValue = isCompleteDate(date, mask) && isValidDate(parsedDate, minYear, maxYear)
            ? format(parsedDate, 'yyyy-MM-dd')
            : '';
        if (newValue !== value) {
            onChangeValue === null || onChangeValue === void 0 ? void 0 : onChangeValue(newValue);
        }
    }, [date, dateFormat, minYear, maxYear, mask, value, onChangeValue]);
    useEffect(() => {
        if (!date && value) {
            setDate(i18n.date(value, { format: dateFormat }));
            return;
        }
        handleDateValueChange();
    }, [date, dateFormat, handleDateValueChange, value, i18n]);
    return (_jsx(Input, Object.assign({}, inputProps, { value: date, onChange: handleDateChange, placeholder: dateFormatLowerCase, mask: mask, pipe: pipe, inputMode: InputMode.Numeric })));
});
DateInput.displayName = 'DateInput';
const isValidDate = (parsedDate, minYear, maxYear) => {
    const startOfMinYear = new Date(minYear, 0, 1);
    const endOfMaxYear = new Date(maxYear, 11, 31);
    return (isValid(parsedDate) &&
        isWithinInterval(parsedDate, {
            start: startOfMinYear,
            end: endOfMaxYear
        }));
};
const isCompleteDate = (date, mask) => {
    return mask.every((regex, index) => new RegExp(regex).test(date.charAt(index)));
};
const getDateFormatMask = (dateFormat) => {
    const hasLettersOnly = /([A-Za-z])/;
    return dateFormat.split('').map((char) => {
        return hasLettersOnly.test(char) ? /\d/ : char;
    });
};
const parseDateString = (dateString, dateFormat) => parse(dateString, dateFormat.replace(/Y/g, 'y').replace(/D/g, 'd'), new Date());
//# sourceMappingURL=date-input.js.map